import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  calculateTransactionalPricing, 
  formatTransactionalEmailCount, 
  createTransactionalQuoteData, 
  getTransactionalPricingSlabs,
  TransactionalPricingResult
} from '../../utils/transactionalPricingCalculator';
import TransactionalPriceResults from './TransactionalPriceResults';
import TransactionalQuoteModal from './TransactionalQuoteModal';
import { useUser } from '../../contexts/UserContext';
import './TransactionalPriceCalculator.css';

const TransactionalPriceCalculator: React.FC = () => {
  const [emailCount, setEmailCount] = useState<string>('');
  const [currency, setCurrency] = useState<'INR' | 'USD'>('USD');
  const [dedicatedIpOption, setDedicatedIpOption] = useState<'none' | 'single' | 'multiple'>('none');
  const [dedicatedIpCount, setDedicatedIpCount] = useState(0);
  const [customerName, setCustomerName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [discountInputValue, setDiscountInputValue] = useState<string>('0');
  const [discountReason, setDiscountReason] = useState<string>('');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [pricingResult, setPricingResult] = useState<TransactionalPricingResult | null>(null);
  const { user } = useUser();

  const emailCountNumber = useMemo(() => {
    const num = parseInt(emailCount.replace(/,/g, ''), 10);
    return isNaN(num) ? 0 : num;
  }, [emailCount]);

  // Load pricing slabs on component mount
  useEffect(() => {
    const loadPricingSlabs = async () => {
      setIsLoadingPricing(true);
      setPricingError(null);
      try {
        // Load pricing slabs to validate they exist
        await Promise.all([
          getTransactionalPricingSlabs('INR'),
          getTransactionalPricingSlabs('USD')
        ]);
      } catch (error) {
        setPricingError('Unable to load pricing data. Please check your connection.');
        console.error('Failed to load transactional pricing slabs:', error);
      } finally {
        setIsLoadingPricing(false);
      }
    };

    loadPricingSlabs();
  }, []);

  // Calculate pricing when email count, currency, or dedicated IP changes
  useEffect(() => {
    const calculatePricingAsync = async () => {
      if (emailCountNumber > 0 && !isLoadingPricing) {
        try {
          const result = await calculateTransactionalPricing(emailCountNumber, currency, dedicatedIpCount, discountPercentage);
          setPricingResult(result);
        } catch (error) {
          console.error('Failed to calculate transactional pricing:', error);
          setPricingResult(null);
        }
      } else {
        setPricingResult(null);
      }
    };

    calculatePricingAsync();
  }, [emailCountNumber, currency, dedicatedIpCount, discountPercentage, isLoadingPricing]);

  const handleEmailCountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
    if (value === '') {
      setEmailCount('');
      return;
    }
    
    const num = parseInt(value, 10);
    if (num > 1000000000) return; // Max 100 crore emails
    
    // Format with commas
    const formatted = new Intl.NumberFormat('en-IN').format(num);
    setEmailCount(formatted);
  }, []);

  const handleCurrencyToggle = useCallback(() => {
    setCurrency(prev => prev === 'INR' ? 'USD' : 'INR');
  }, []);

  const handlePresetClick = useCallback((count: number) => {
    const formatted = new Intl.NumberFormat('en-IN').format(count);
    setEmailCount(formatted);
  }, []);

  const handleDedicatedIpOptionChange = useCallback((option: 'none' | 'single' | 'multiple') => {
    setDedicatedIpOption(option);
    if (option === 'none') {
      setDedicatedIpCount(0);
    } else if (option === 'single') {
      setDedicatedIpCount(1);
    } else if (option === 'multiple' && dedicatedIpCount <= 1) {
      setDedicatedIpCount(2); // Default to 2 for multiple
    }
  }, [dedicatedIpCount]);

  const handleDedicatedIpCountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setDedicatedIpCount(2); // Reset to minimum when cleared
      return;
    }
    
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      // Clamp the value between 2 and 10
      const clampedValue = Math.max(2, Math.min(10, numValue));
      setDedicatedIpCount(clampedValue);
    }
  }, []);

  const handleDiscountPercentageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Always update the display value for immediate feedback
    setDiscountInputValue(value);
    
    // Handle empty value
    if (value === '' || value === '0') {
      setDiscountPercentage(0);
      return;
    }
    
    // Validate and update the actual discount percentage used in calculations
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 50) {
      setDiscountPercentage(numValue);
    }
  }, []);

  const handleDiscountInputBlur = useCallback(() => {
    // On blur, ensure the input shows the actual discount percentage value
    const displayValue = discountPercentage === 0 ? '0' : discountPercentage.toString();
    setDiscountInputValue(displayValue);
  }, [discountPercentage]);

  const handleDiscountReasonChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setDiscountReason(e.target.value);
  }, []);

  const handleGenerateQuote = useCallback(() => {
    if (emailCountNumber > 0) {
      setShowQuoteModal(true);
    }
  }, [emailCountNumber]);

  const quoteData = useMemo(() => {
    if (emailCountNumber > 0 && pricingResult) {
      return createTransactionalQuoteData(
        customerName || 'Valued Customer',
        companyName || 'Your Company',
        emailCountNumber,
        pricingResult,
        discountReason || undefined,
        user ? { name: user.name || 'Sales Team', email: user.email || 'sales@mailercloud.com' } : undefined
      );
    }
    return null;
  }, [customerName, companyName, emailCountNumber, pricingResult, discountReason, user]);

  const presetValues = [50000, 100000, 500000, 1000000, 2000000, 5000000]; // 50K, 100K, 500K, 1M, 2M, 5M

  // Show loading state
  if (isLoadingPricing) {
    return (
      <div className="transactional-price-calculator" role="main" aria-label="Transactional Email Price Calculator">
        <div className="calculator-container">
          <div className="calculator-header">
            <h1>Transactional Email Price Calculator</h1>
            <p className="subtitle">SMTP/API Pricing</p>
            <p>Loading pricing data...</p>
          </div>
          <div className="calculator-card">
            <div className="loading-spinner">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (pricingError) {
    return (
      <div className="transactional-price-calculator" role="main" aria-label="Transactional Email Price Calculator">
        <div className="calculator-container">
          <div className="calculator-header">
            <h1>Transactional Email Price Calculator</h1>
            <p className="subtitle">SMTP/API Pricing</p>
          </div>
          <div className="calculator-card">
            <div className="error-message">
              <p>{pricingError}</p>
              <button onClick={() => window.location.reload()} className="retry-btn">
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="transactional-price-calculator" role="main" aria-label="Transactional Email Price Calculator">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <h1>Transactional Email Price Calculator</h1>
          <p className="subtitle">SMTP/API Pricing</p>
          <p>Calculate your monthly cost for transactional emails sent via SMTP or API</p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-form">
            {/* Email Count and Currency Row */}
            <div className="compact-row">
              <div className="input-group email-count-group">
                <label htmlFor="email-count" className="input-label">
                  Number of Emails per Month
                </label>
                <div className="input-wrapper">
                  <input
                    id="email-count"
                    type="text"
                    value={emailCount}
                    onChange={handleEmailCountChange}
                    placeholder="Enter email count"
                    className="email-input"
                    aria-describedby="email-count-help"
                  />
                  <div className="input-suffix">emails/month</div>
                </div>
                <div id="email-count-help" className="input-help">
                  Enter the number of transactional emails you plan to send monthly
                </div>
              </div>

              {/* Currency Toggle */}
              <div className="currency-toggle compact">
                <span className="currency-label">Currency:</span>
                <button
                  onClick={handleCurrencyToggle}
                  className="currency-btn"
                  type="button"
                  aria-label={`Switch to ${currency === 'INR' ? 'USD' : 'INR'}`}
                >
                  <span className={`currency-option ${currency === 'USD' ? 'active' : ''}`}>$ USD</span>
                  <span className="currency-divider">|</span>
                  <span className={`currency-option ${currency === 'INR' ? 'active' : ''}`}>₹ INR</span>
                </button>
              </div>
            </div>

            {/* Dedicated IP Options */}
            <div className="dedicated-ip-section">
              <h3 className="section-title">Dedicated IP Options</h3>
              <div className="ip-options">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="dedicatedIp"
                    value="none"
                    checked={dedicatedIpOption === 'none'}
                    onChange={() => handleDedicatedIpOptionChange('none')}
                    className="radio-input"
                  />
                  <span className="radio-text">
                    No Dedicated IP
                    <span className="radio-info">Use shared IP pool</span>
                  </span>
                </label>

                <label className="radio-label">
                  <input
                    type="radio"
                    name="dedicatedIp"
                    value="single"
                    checked={dedicatedIpOption === 'single'}
                    onChange={() => handleDedicatedIpOptionChange('single')}
                    className="radio-input"
                  />
                  <span className="radio-text">
                    Single Dedicated IP
                    <span className="radio-info">
                      {currency === 'USD' ? '$50' : '₹4,150'}/month
                    </span>
                  </span>
                </label>

                <label className="radio-label">
                  <input
                    type="radio"
                    name="dedicatedIp"
                    value="multiple"
                    checked={dedicatedIpOption === 'multiple'}
                    onChange={() => handleDedicatedIpOptionChange('multiple')}
                    className="radio-input"
                  />
                  <span className="radio-text">
                    Multiple Dedicated IPs
                    <span className="radio-info">
                      {currency === 'USD' ? '$50' : '₹4,150'} each/month
                    </span>
                  </span>
                </label>
              </div>

              {dedicatedIpOption === 'multiple' && (
                <div className="ip-count-input">
                  <label htmlFor="ip-count" className="input-label">
                    Number of IPs (2-10)
                  </label>
                  <input
                    id="ip-count"
                    type="number"
                    min="2"
                    max="10"
                    value={dedicatedIpCount}
                    onChange={handleDedicatedIpCountChange}
                    className="ip-count-field"
                  />
                  <p className="input-help">
                    Total cost: {currency === 'USD' ? `$${dedicatedIpCount * 50}` : `₹${dedicatedIpCount * 4150}`}/month
                  </p>
                </div>
              )}

              <p className="section-help">
                Dedicated IPs improve deliverability and sender reputation. Multiple IPs are useful for different domains or high-volume sending.
              </p>
            </div>

            {/* Discount Section */}
            <div className="discount-section">
              <h3 className="section-title">Discount Options</h3>
              <p className="section-help">
                Apply discounts to email costs only. Dedicated IP pricing remains fixed.
              </p>
              
              <div className="discount-fields-grid">
                <div className="input-group compact">
                  <label htmlFor="discount-percentage" className="input-label">
                    Discount Percentage (0-50%)
                  </label>
                  <div className="input-wrapper">
                    <input
                      id="discount-percentage"
                      type="number"
                      min="0"
                      max="50"
                      step="0.1"
                      value={discountInputValue}
                      onChange={handleDiscountPercentageChange}
                      onBlur={handleDiscountInputBlur}
                      placeholder="0"
                      className="discount-input"
                    />
                    <div className="input-suffix">%</div>
                  </div>
                  <div className="input-help">
                    Maximum 50% discount allowed on email costs
                  </div>
                </div>

                <div className="input-group compact">
                  <label htmlFor="discount-reason" className="input-label">
                    Discount Reason
                  </label>
                  <select
                    id="discount-reason"
                    value={discountReason}
                    onChange={handleDiscountReasonChange}
                    className="discount-select"
                  >
                    <option value="">Select reason</option>
                    <option value="volume_discount">Volume Discount</option>
                    <option value="enterprise_plan">Enterprise Plan</option>
                    <option value="partnership">Partnership Agreement</option>
                    <option value="promotional">Promotional Offer</option>
                    <option value="loyalty_discount">Loyalty Discount</option>
                    <option value="custom">Custom Agreement</option>
                  </select>
                  <div className="input-help">
                    Select the reason for applying discount
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Information - Compact 2-Column */}
            <div className="customer-info-compact">
              <div className="customer-fields-grid">
                <div className="input-group compact">
                  <label htmlFor="customer-name" className="input-label">
                    Customer Name
                  </label>
                  <input
                    id="customer-name"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Customer name"
                    className="customer-input"
                  />
                </div>

                <div className="input-group compact">
                  <label htmlFor="company-name" className="input-label">
                    Company Name
                  </label>
                  <input
                    id="company-name"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Company name"
                    className="customer-input"
                  />
                </div>
              </div>
            </div>

            {/* Preset Buttons */}
            <div className="preset-buttons">
              <span className="preset-label">Quick Select:</span>
              {presetValues.map(value => (
                <button
                  key={value}
                  onClick={() => handlePresetClick(value)}
                  className={`preset-btn ${emailCountNumber === value ? 'active' : ''}`}
                  type="button"
                >
                  {formatTransactionalEmailCount(value)}
                </button>
              ))}
            </div>
          </div>

          {/* Results */}
          {emailCountNumber > 0 && pricingResult && (
            <TransactionalPriceResults 
              result={pricingResult}
              emailCount={emailCountNumber}
            />
          )}

          {/* Generate Quote Button */}
          {emailCountNumber > 0 && pricingResult && (
            <div className="generate-quote-section">
              <button
                onClick={handleGenerateQuote}
                className="generate-quote-btn"
                type="button"
              >
                <span className="quote-btn-icon">📄</span>
                Generate Professional Quote
              </button>
              <p className="quote-help-text">
                Create a professional quotation with Mailercloud branding for your client presentation
              </p>
            </div>
          )}
        </div>

        {/* Pricing Info Section */}
        <div className="pricing-info-section">
          <h3>Pricing Structure</h3>
          <div className="pricing-tiers">
            <div className="pricing-tier">
              <h4>Standard Volume</h4>
              <p className="tier-range">Up to 1.5 Million emails/month</p>
              <p className="tier-price">{currency === 'USD' ? '$0.20' : '₹16.60'} per 1,000 emails</p>
            </div>
            <div className="pricing-tier">
              <h4>High Volume</h4>
              <p className="tier-range">Above 1.5 Million emails/month</p>
              <p className="tier-price">{currency === 'USD' ? '$0.18' : '₹14.94'} per 1,000 emails</p>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-card">
            <h3>Transactional Email Features</h3>
            <ul>
              <li>Real-time email delivery via SMTP/API</li>
              <li>99.9% uptime guarantee</li>
              <li>Detailed delivery reports</li>
              <li>Webhooks for real-time events</li>
              <li>Advanced email tracking</li>
            </ul>
          </div>
          
          <div className="info-card">
            <h3>Use Cases</h3>
            <ul>
              <li>Order confirmations</li>
              <li>Password resets</li>
              <li>Account notifications</li>
              <li>Shipping updates</li>
              <li>System alerts</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Quote Modal */}
      {quoteData && (
        <TransactionalQuoteModal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          quoteData={quoteData}
        />
      )}
    </div>
  );
};

export default TransactionalPriceCalculator;